import {useEffect, useState} from 'react'
import classNames from 'classnames'
import take from 'lodash/take.js'
import sortBy from 'lodash/sortBy.js'
import head from 'lodash/head.js'
import subDays from 'date-fns/subDays'
import {createSelector} from 'reselect'

import {
  activityTasksSelector,
  checkRunningTasks,
  getAllTasksDebounced,
  taskListSelector,
  VISIBLE_ACTIVITY_COUNT,
} from '../../../data/tasks.js'
import {
  debouncedSaveUserSettings,
  isSuperuserSelector,
  userSettingSelector,
} from '../../../data/me.js'
import {currentDropdownSelector} from '../../../redux/selectors/ui/index.js'

import ActivityMenuItem from './ActivityMenuItem.js'
import {
  REPORTS_PANEL_ACTIVITY,
  panelLinkSelector,
} from '../../ReportsPage/reportsPageSelectors.js'
import Link from '../../../common/components/Link/Link.js'
import Icon from '../../../common/icons/Icon.js'
import {currentDateSelector} from '../../CurrentDateListener.js'
import {useSelector} from '../../../store.js'
import {taskWebsocketsSelector} from '../../../data/company.js'

const ACTIVITY_MENU_DROPDOWN = 'ACTIVITY_MENU_DROPDOWN'
const ACTIVITY_MENU__LAST_VIEWED_TIME = 'activity_menu.last_viewed_time'

function activityMenuLastViewedTimeSelector(state) {
  return userSettingSelector(state, {
    setting: ACTIVITY_MENU__LAST_VIEWED_TIME,
    defaultValue: '2010-01-01T00:00:00.000',
  })
}

async function saveActivityMenuLastViewedTime() {
  debouncedSaveUserSettings({
    [ACTIVITY_MENU__LAST_VIEWED_TIME]: new Date().toISOString(),
  })
}

export const hasUnreadTasksSelector = createSelector(
  activityMenuLastViewedTimeSelector,
  taskListSelector,
  (lastViewedTime, tasks) => {
    const latest = head(
      tasks.map((task) => new Date(task.updated)).sort((a, b) => b - a),
    )
    return new Date(lastViewedTime).getTime() < new Date(latest).getTime()
  },
)

export const exportTasksSelector = createSelector(
  currentDateSelector,
  taskListSelector,
  (currentDate, tasks) => {
    const minExportDate = subDays(currentDate, '7')

    return take(
      sortBy(
        tasks.filter(
          (task) =>
            task._linkdata &&
            new Date(task.created).getTime() > minExportDate.getTime(),
        ),
        'created',
      ).reverse(),
      VISIBLE_ACTIVITY_COUNT,
    )
  },
)

export default function ActivityMenu() {
  const activityTasks = useSelector(activityTasksSelector)
  const exportTasks = useSelector(exportTasksSelector)
  const isVisible =
    useSelector(currentDropdownSelector) === ACTIVITY_MENU_DROPDOWN
  const hasUnreadTasks = useSelector(hasUnreadTasksSelector)
  const isSuperuser = useSelector(isSuperuserSelector)
  const reportsPanelActivityURL = useSelector((state) =>
    panelLinkSelector(state, {panel: REPORTS_PANEL_ACTIVITY}),
  )
  const taskWebsockets = useSelector(taskWebsocketsSelector)
  const [latch, setLatch] = useState(false)

  useEffect(() => {
    // don't do anything on component mount, wait for next render
    if (!latch) {
      return
    }

    // save on open and close
    saveActivityMenuLastViewedTime()

    if (!isVisible) {
      return
    }

    if (taskWebsockets) {
      // only request tasks on open
      getAllTasksDebounced()
    } else {
      checkRunningTasks()
    }
  }, [isVisible])

  useEffect(() => {
    // only set on component mount
    setLatch(true)
  }, [])

  return (
    <li
      className={classNames(
        'utility-nav__item wrap--dropdown wrap--activities dropdown',
        {
          open: isVisible,
          'unread-activity': hasUnreadTasks,
        },
      )}
      data-dropdown={ACTIVITY_MENU_DROPDOWN}
    >
      <button
        className="btn btn--ko-dropdown utility-nav__link dropdown"
        title="Activity"
      >
        <Icon name="sym-activity" className="fs-01" />
      </button>
      <div className="wrap--inner-dropdown wrap--inner-dropdown-right">
        <div
          className="list--dropdown list--activity-dropdown"
          data-dropdown-prevent-close
        >
          <ul className="list--no-style">
            <li className="wrap--activity-header flex flex--justify">
              <strong>Activity</strong>
              <Link
                className="fs-n0 v-align-base"
                href={reportsPanelActivityURL}
              >
                <strong>Exports</strong> →
              </Link>
            </li>
            {activityTasks
              .filter((task) => !task._linkdata)
              .map((task) => (
                <ActivityMenuItem
                  task={task}
                  key={task.id}
                  showLogLink={isSuperuser}
                />
              ))}
          </ul>
          {activityTasks.length === 0 && exportTasks.length === 0 && (
            <div className="list__item list__item--dropdown no-activity">
              <dl className="list--activity-item list--no-activity">
                <dt className="list__title--activity">
                  This is your activity menu.
                </dt>
                <dd className="list__item--activity">
                  There are many like it, but this one is yours...
                </dd>
                <dd className="list__item--activity">
                  Check here for the status on order and inventory imports from
                  your shopping cart and marketplace.
                </dd>
              </dl>
            </div>
          )}
        </div>
      </div>
    </li>
  )
}
